import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { harnessInit } from 'adc-harness-state';
import { FeatureFlagProvider, whenFlagsReady } from '../helpers/featureFlags/FirebaseFeatureFlags';
import getConfig from '../config';
import PermsError from './Errors/PermsError';
import AppComp from './App';
import GenericError from './Errors/GenericError';
import DefaultLayout from './Layouts/DefaultLayout';
import FullPageError from '../components/FullPageError';
import featureFlags, { flagEnabled } from '../helpers/featureFlags';
import { getAuth, addRefreshAuthListener, checkSafeConnections } from '../actions/auth';
import { resetUserMessages } from '../actions/userMessages';
import ErrorBoundary from '../components/ErrorBoundary';
import XapInterstitial from './XapInterstitial';
import isMobilePage from '../helpers/mobile';
import FeatureShingles from '../components/FeatureShingles';
import PageApp from './Layouts/PageApp';
import ExtendSessionDialog from '../components/ExtendSessionDialog';
import CustomLoading from '../components/CustomLoading';

const { firebaseEnabled } = getConfig();

const RenderApp = (props) => {
  const {
    loading = true,
    xmAccountLoading,
    permsError,
    redirect,
    error,
    errorData,
    isHarness,
    harnessLoading,
    location,
  } = props;
  if (harnessLoading) { return null; }
  if (error) {
    if (flagEnabled('xmPaymentReskinEnabled')) {
      return (
        <PageApp>
          <DefaultLayout
            component={FullPageError}
            error={errorData}
            iconName="info"
            title="Something unexpected happened."
            subtitle="Try refreshing the page. If the problem persists, please check back later."
          />
        </PageApp>
      );
    }
    return (
      <PageApp>
        <DefaultLayout
          component={GenericError}
          isHarness={isHarness}
          error={errorData}
        />
      </PageApp>
    );
  }

  if (permsError) {
    return (
      <PageApp>
        <DefaultLayout
          component={PermsError}
          isHarness={isHarness}
          error="perms_error"
        />
      </PageApp>
    );
  }

  if (loading || redirect || (isMobilePage(location.pathname) && xmAccountLoading)) {
    return <CustomLoading />;
  }

  if (firebaseEnabled) {
    return (
      <ErrorBoundary isHarness={isHarness}>
        <FeatureFlagProvider fallback={<CustomLoading />}>
          <AppComp {...props} />
          <XapInterstitial location={location} />
        </FeatureFlagProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary isHarness={isHarness}>
      <FeatureShingles location={location} />
      <AppComp {...props} />
      <XapInterstitial location={location} />
    </ErrorBoundary>
  );
};

export class Auth extends Component {
  constructor() {
    super();
    this.state = {
      authLoaded: false,
      featureFlagsReady: false,
    };

    // Defer to the next tick so setState never fires synchronously during construction.
    const setFlagsReady = () => Promise.resolve().then(() => {
      this.setState({ featureFlagsReady: true });
    });

    if (featureFlags) {
      featureFlags.onReady(setFlagsReady);
    } else if (firebaseEnabled) {
      // featureFlags (DarkSwitch) is null when firebaseEnabled; wait on the Firebase cache instead.
      whenFlagsReady().then(setFlagsReady);
    }
  }

  componentDidMount() {
    const { handleHarnessInit, location, handleAddRefreshAuthListener } = this.props;

    handleHarnessInit(location);
    handleAddRefreshAuthListener();
  }

  componentDidUpdate(prevProps) {
    const {
      location,
      handleGetAuth,
      device,
      handleResetUserMessages,
      handleCheckSafeConnections,
      messageShown,
      loading,
    } = this.props;
    const { authLoaded, featureFlagsReady } = this.state;

    if (!authLoaded && featureFlagsReady) {
      // Don't attempt to getAuth until the feature flags are ready
      // because the getAuth action and reducer are segmented with flags.
      handleGetAuth(device);
      // eslint-disable-next-line
      this.setState({ authLoaded: true });
    }

    // Check Safe Connections for XM pages after auth loading completes
    // when loading changes from true to false, auth is completed successfully
    if (prevProps.loading && !loading) {
      handleCheckSafeConnections();
    }

    if (location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);

      if (messageShown) {
        handleResetUserMessages();
      }
    }
  }

  render() {
    return (
      <div id="page-content" className="page-content">
        <RenderApp {...this.props} />
        <ExtendSessionDialog />
      </div>
    );
  }
}

const mapStateToProps = ({
  auth: {
    crsId,
    loading,
    redirect,
    error,
    errorData,
    permsError,
    xmAccount: {
      loading: xmAccountLoading,
    } = {},
  },
  harness: { loading: harnessLoading, device, isHarness },
  userMessages: { messageShown },
}) => ({
  crsId,
  loading,
  xmAccountLoading,
  redirect,
  error,
  errorData,
  permsError,
  isHarness,
  device,
  harnessLoading,
  messageShown,
});

const mapDispatchToProps = dispatch => ({
  handleGetAuth: bindActionCreators(getAuth, dispatch),
  handleAddRefreshAuthListener: bindActionCreators(addRefreshAuthListener, dispatch),
  handleResetUserMessages: bindActionCreators(resetUserMessages, dispatch),
  handleCheckSafeConnections: bindActionCreators(checkSafeConnections, dispatch),
  handleHarnessInit: location => dispatch(harnessInit(location)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Auth));
